import { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import EmailIcon from '@mui/icons-material/Email';
import PrintIcon from '@mui/icons-material/Print';
import RefreshIcon from '@mui/icons-material/Refresh';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import QRCodeData from '../ui/grcode/qrCodeData';
import {
  getClubPortalContacts,
  getManagedClubContacts,
  getSecretaryPortalContacts,
  portalErrorMessage
} from '../../features/portal/portalService';
import { formatPortalDate } from './portalPaths';

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const PortalAccessDialog = ({
  open,
  onClose,
  mode = 'admin',
  token = '',
  eventId = '',
  eventName,
  scopeLabel,
  url,
  pin,
  pinSet,
  pinExisting = false,
  pinRecoverable = null,
  pinLoading = false,
  pinUpdatedAt,
  email = '',
  shared = false,
  eventCount = 1,
  scopeKind = 'club',
  venueId = null,
  workspaceScope = null,
  busy = false,
  error,
  onRegenerate,
  onSendEmail,
  onSendWhatsApp
}) => {
  const [copyMessage, setCopyMessage] = useState('');
  const [confirmRegenerate, setConfirmRegenerate] = useState(false);
  const [emailValue, setEmailValue] = useState('');
  const [emailError, setEmailError] = useState('');
  const [emailStatus, setEmailStatus] = useState('');
  const [sendingEmail, setSendingEmail] = useState(false);
  const [whatsAppStatus, setWhatsAppStatus] = useState('');
  const [whatsAppError, setWhatsAppError] = useState('');
  const [sendingWhatsApp, setSendingWhatsApp] = useState(false);
  const [contacts, setContacts] = useState([]);
  const [contactsLoading, setContactsLoading] = useState(false);
  const [contactsError, setContactsError] = useState('');
  const [contactsReload, setContactsReload] = useState(0);
  const [selectedContactId, setSelectedContactId] = useState('');
  const wasOpen = useRef(false);

  useEffect(() => {
    if (open && !wasOpen.current) {
      setEmailValue(email || '');
      setEmailError('');
      setEmailStatus('');
      setWhatsAppStatus('');
      setWhatsAppError('');
    }

    if (!open) {
      setCopyMessage('');
      setConfirmRegenerate(false);
      setEmailError('');
      setEmailStatus('');
      setSendingEmail(false);
      setWhatsAppStatus('');
      setWhatsAppError('');
      setSendingWhatsApp(false);
    }

    wasOpen.current = open;
  }, [open, email]);

  useEffect(() => {
    if (!open) return undefined;

    let cancelled = false;
    setContacts([]);
    setContactsLoading(true);
    setContactsError('');
    setSelectedContactId('');

    let request;
    if (mode === 'portal') {
      request = getManagedClubContacts(token, venueId);
    } else if (scopeKind === 'secretary') {
      request = getSecretaryPortalContacts(eventId);
    } else {
      request = getClubPortalContacts(eventId, venueId);
    }

    request
      .then((response) => {
        if (!cancelled) setContacts(response?.contacts || []);
      })
      .catch((requestError) => {
        if (!cancelled) {
          setContactsError(
            portalErrorMessage(
              requestError,
              'The saved contacts could not be loaded.'
            )
          );
        }
      })
      .finally(() => {
        if (!cancelled) setContactsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open, contactsReload, mode, token, eventId, scopeKind, venueId]);

  const selectedContact =
    contacts.find((contact) => contact.id === selectedContactId) || null;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopyMessage('Link copied.');
    } catch {
      setCopyMessage('Copy is not available here. Select the link above and copy it.');
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const shareLines = (contactName = '') => {
    const lines = [];

    if (contactName) lines.push(`Hi ${contactName},`);
    lines.push(
      `BowlScout scoring portal for ${eventName || 'this event'}`,
      `${scopeLabel}: ${url}`,
      coverage
    );

    if (pin) lines.push(`PIN: ${pin}`);

    return lines;
  };

  const openWhatsApp = (phone = '', contactName = '') => {
    const digits = String(phone || '').replace(/\D/g, '');
    const target = digits ? `https://wa.me/${digits}` : 'https://wa.me/';

    window.open(
      `${target}?text=${encodeURIComponent(shareLines(contactName).join('\n'))}`,
      '_blank',
      'noopener,noreferrer'
    );
  };

  const handleWhatsApp = () => {
    if (pinLoading) return;
    openWhatsApp();
  };

  const handleContactWhatsApp = async () => {
    if (!selectedContact?.phone || sendingWhatsApp || pinLoading) return;

    // Open the window synchronously so the browser treats it as part of the
    // click. Without this handle the share cannot open after issuing a PIN,
    // and no PIN is issued when the pop-up is blocked.
    const shareWindow = window.open('', '_blank');
    if (!shareWindow) {
      setWhatsAppError(
        'Allow pop-ups for BowlScout to open WhatsApp, then try again.'
      );
      setWhatsAppStatus('');
      return;
    }
    try {
      shareWindow.opener = null;
    } catch {
      // Cross-window access can be blocked; the navigation below still works.
    }

    setSendingWhatsApp(true);
    setWhatsAppError('');
    setWhatsAppStatus('');

    try {
      let result = null;
      let issueFailed = false;

      if (!selectedContact.legacy && onSendWhatsApp) {
        try {
          result = await onSendWhatsApp(selectedContact);
        } catch {
          issueFailed = true;
        }
      }

      const personalPin = result?.personal ? String(result.pin || '') : '';
      const link = result?.portalLink || url;
      let message = shareLines(selectedContact.name).join('\n');

      if (personalPin) {
        message = [
          `Hi ${selectedContact.name || 'there'},`,
          `BowlScout scoring portal for ${eventName || 'this event'}`,
          `${scopeLabel}: ${link}`,
          `Sign in with your personal PIN: ${personalPin}`,
          'Your PIN identifies you, so there is no need to enter an email address.',
          'Keep this PIN to yourself.'
        ].join('\n');
      }

      const digits = String(selectedContact.phone || '').replace(/\D/g, '');
      const target = digits ? `https://wa.me/${digits}` : 'https://wa.me/';
      shareWindow.location.href = `${target}?text=${encodeURIComponent(message)}`;

      if (personalPin) {
        setWhatsAppStatus(
          result?.reused
            ? `WhatsApp opened with ${
                selectedContact.name || selectedContact.email
              }'s existing personal PIN.`
            : `A personal PIN was issued for ${
                selectedContact.name || selectedContact.email
              } and opened in WhatsApp. It replaces their previous PIN.`
        );
      } else {
        setWhatsAppStatus(
          issueFailed
            ? 'WhatsApp opened with the shared portal link and PIN.'
            : 'WhatsApp opened with the portal link and shared PIN.'
        );
      }
    } finally {
      setSendingWhatsApp(false);
    }
  };

  const handleSendEmail = async () => {
    const trimmed = emailValue.trim();

    if (!EMAIL_PATTERN.test(trimmed)) {
      setEmailError('Enter a valid email address.');
      return;
    }

    if (!onSendEmail) {
      setEmailError('Email is not available here.');
      return;
    }

    const matchedContact =
      selectedContact &&
      selectedContact.email.trim().toLowerCase() === trimmed.toLowerCase()
        ? selectedContact
        : null;

    setSendingEmail(true);
    setEmailError('');
    setEmailStatus('');
    setWhatsAppStatus('');
    setWhatsAppError('');

    try {
      const result = await onSendEmail(trimmed, matchedContact?.name || '');
      setEmailValue(trimmed);

      if (result?.personal) {
        setEmailStatus(
          result?.reused
            ? `Existing personal PIN re-sent to ${trimmed}.`
            : `Personal PIN emailed to ${trimmed}.`
        );
      } else if (result?.warning) {
        setEmailStatus(`Email sent to ${trimmed}. ${result.warning}`);
      } else {
        setEmailStatus(`Email sent to ${trimmed}.`);
      }
    } catch (sendError) {
      setEmailError(
        sendError?.response?.data?.message ||
          sendError?.message ||
          'The email could not be sent. Please try again.'
      );
    } finally {
      setSendingEmail(false);
    }
  };

  const handleRegenerate = () => {
    if (pinSet || pin) {
      setConfirmRegenerate(true);
      return;
    }
    onRegenerate?.();
  };

  const count = Number(eventCount) || 1;
  const eventWord = `${count} ${count === 1 ? 'event' : 'events'}`;
  const noWorkspace = workspaceScope === 'none';
  let coverage = 'This PIN is for this event only.';
  if (shared) {
    if (scopeKind === 'secretary') {
      coverage = noWorkspace
        ? `This PIN works at every no-workspace event (${eventWord}). After signing in, use the event picker to switch events.`
        : `This PIN works at every event in this workspace (${eventWord}). After signing in, use the event picker to switch events.`;
    } else {
      coverage = noWorkspace
        ? `This PIN works at every no-workspace event at this club (${eventWord}). After signing in, use the event picker to switch events.`
        : `This PIN works at every event at this club in this workspace (${eventWord}). After signing in, use the event picker to switch events.`;
    }
  }

  let regenerateLabel = 'Generate PIN';
  if (shared) {
    regenerateLabel = pinSet || pin ? 'Regenerate PIN' : 'Generate PIN';
  } else {
    regenerateLabel =
      scopeKind === 'secretary' ? 'Create workspace-wide PIN' : 'Create club-wide PIN';
  }

  let scopeName =
    scopeKind === 'secretary' ? 'every event in this workspace' : 'every event at this club';
  if (noWorkspace) {
    scopeName =
      scopeKind === 'secretary'
        ? 'every no-workspace event'
        : 'every no-workspace event at this club';
  }
  let confirmTitle = 'Create a shared PIN?';
  let confirmBody = `Generating a shared PIN replaces this event's PIN with one that works at ${scopeName}. Devices signed in with the old PIN will be signed out.`;
  let confirmAction = 'Create PIN';
  if (shared) {
    confirmTitle = 'Regenerate this shared PIN?';
    confirmBody = `A new PIN replaces the old one at every event in its scope (${eventWord}). Every device already signed in will be signed out and must use the new PIN.`;
    confirmAction = 'Regenerate';
  }
  const updated = formatPortalDate(pinUpdatedAt);

  return (
    <>
      <style>
        {`
          .portal-print-sheet { display: none; }
          @media print {
            @page { margin: 14mm; }
            body * { visibility: hidden !important; }
            .portal-print-sheet, .portal-print-sheet * { visibility: visible !important; }
            .portal-print-sheet {
              display: block !important;
              position: absolute;
              left: 0;
              top: 0;
              width: 100%;
              color: #000 !important;
              background: #fff !important;
            }
          }
        `}
      </style>

      <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="sm" fullWidth>
        <DialogTitle>{scopeLabel} portal</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            {eventName}
          </Typography>

          {pin ? (
            <Paper
              variant="outlined"
              sx={{ p: 2, mt: 1, mb: 2, textAlign: 'center', bgcolor: 'action.hover' }}>
              <Typography variant="overline" color="text.secondary">
                {pinExisting ? 'Current PIN' : 'New PIN'}
              </Typography>
              <Typography
                aria-label={`PIN ${pin}`}
                sx={{
                  fontFamily: 'monospace',
                  fontSize: { xs: 40, sm: 52 },
                  fontWeight: 700,
                  letterSpacing: { xs: '0.18em', sm: '0.25em' },
                  lineHeight: 1.2
                }}>
                {pin}
              </Typography>
            </Paper>
          ) : (
            <Alert severity="info" sx={{ mt: 1, mb: 2 }}>
              {pinLoading
                ? 'Loading the current PIN…'
                : pinSet
                  ? pinRecoverable === false
                    ? 'This portal has a PIN, but BowlScout cannot show it. Regenerate it once to enable PIN resend; the new PIN can then be sent again later.'
                    : 'This portal already has a PIN. BowlScout could not load it right now. Try again, or regenerate it if it has been lost.'
                  : 'No PIN has been set yet. Generate one so officials can sign in.'}
            </Alert>
          )}

          {pin && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              {pinExisting
                ? 'This is the current PIN. You can send it now, or come back to Link & QR and send it again without replacing it. Keep it private.'
                : pinRecoverable === false
                  ? 'Share this PIN now, by WhatsApp or email, or write it down. PIN resend is not configured, so BowlScout cannot show it again.'
                  : 'This is the new current PIN. Send it now, or come back to Link & QR later and send it again without replacing it. Keep it private.'}
            </Alert>
          )}

          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {coverage}
          </Typography>

          {updated && (
            <Typography variant="caption" color="text.secondary" display="block" mb={2}>
              PIN updated {updated}
            </Typography>
          )}

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems="stretch">
            <TextField
              label="Portal link"
              value={url}
              fullWidth
              size="small"
              InputProps={{ readOnly: true }}
              onFocus={(event) => event.target.select()}
              inputProps={{ 'aria-label': 'Portal link' }}
            />
            <Button
              variant="outlined"
              startIcon={<ContentCopyIcon />}
              onClick={handleCopy}
              sx={{ flexShrink: 0 }}>
              Copy
            </Button>
          </Stack>

          {copyMessage && (
            <Typography variant="caption" color="text.secondary" display="block" mt={0.75}>
              {copyMessage}
            </Typography>
          )}

          <Button
            variant="contained"
            color="success"
            startIcon={<WhatsAppIcon />}
            onClick={handleWhatsApp}
            fullWidth
            disabled={pinLoading}
            sx={{ mt: 1.5 }}>
            Share on WhatsApp
          </Button>

          <Box display="flex" justifyContent="center" my={2}>
            <Paper
              elevation={0}
              sx={{ p: 1.5, bgcolor: '#fff', border: 1, borderColor: 'divider', borderRadius: 2 }}>
              <QRCodeData value={url} style={{ width: 190, height: 190 }} />
            </Paper>
          </Box>

          <Typography variant="body2" color="text.secondary">
            The QR code contains the portal link only, never the PIN. Officials scan it, then enter
            the 5-digit PIN. Their device stays signed in until they log out or the PIN is
            regenerated or disabled.
          </Typography>

          <Divider sx={{ my: 2 }} />

          <Typography variant="subtitle2" fontWeight="bold">
            {pin ? 'Send the link and PIN' : 'Send the link'}
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block" mb={1}>
            Pick a saved contact to email or send on WhatsApp, or type an email address.
          </Typography>

          <TextField
            select
            label="Saved contact"
            value={selectedContactId}
            onChange={(event) => {
              const nextId = event.target.value;
              const contact = contacts.find((entry) => entry.id === nextId);
              setSelectedContactId(nextId);
              setEmailError('');
              setEmailStatus('');
              setWhatsAppStatus('');
              setWhatsAppError('');
              if (contact) setEmailValue(contact.email || '');
            }}
            fullWidth
            size="small"
            disabled={contactsLoading}
            SelectProps={{
              renderValue: (contactId) => {
                const contact = contacts.find((entry) => entry.id === contactId);
                if (!contact) return 'Enter an email address';
                return `${contact.name || contact.email} — ${contact.email}`;
              }
            }}>
            <MenuItem value="">Enter an email address</MenuItem>
            {contacts.map((contact) => (
              <MenuItem key={contact.id} value={contact.id}>
                <Box>
                  <Typography variant="body2" fontWeight={600}>
                    {contact.name || 'Contact'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block">
                    {contact.email}
                    {contact.phone ? ` · ${contact.phone}` : ''}
                  </Typography>
                </Box>
              </MenuItem>
            ))}
          </TextField>

          {contactsLoading && (
            <Typography variant="caption" color="text.secondary" display="block" mt={0.75}>
              Loading saved contacts…
            </Typography>
          )}

          {!contactsLoading && contactsError && (
            <Box mt={0.75}>
              <Typography variant="caption" color="error">
                {contactsError}{' '}
                <Button
                  size="small"
                  onClick={() => setContactsReload((value) => value + 1)}>
                  Retry
                </Button>
              </Typography>
            </Box>
          )}

          {!contactsLoading && !contactsError && contacts.length === 0 && (
            <Typography variant="caption" color="text.secondary" display="block" mt={0.75}>
              No saved contacts yet. Use Contacts to add one.
            </Typography>
          )}

          {selectedContact && !selectedContact.phone && (
            <Typography variant="caption" color="text.secondary" display="block" mt={0.75}>
              Add a phone number in Contacts to use WhatsApp.
            </Typography>
          )}

          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={1}
            alignItems={{ xs: 'stretch', sm: 'flex-start' }}
            mt={1.5}>
            <TextField
              label="Email address"
              type="email"
              value={emailValue}
              onChange={(event) => {
                setEmailValue(event.target.value);
                setSelectedContactId('');
                setEmailError('');
                setEmailStatus('');
                setWhatsAppStatus('');
                setWhatsAppError('');
              }}
              fullWidth
              size="small"
              autoComplete="email"
              inputProps={{ inputMode: 'email' }}
              error={Boolean(emailError)}
              helperText={emailError || ' '}
            />
            <Button
              variant="outlined"
              startIcon={
                sendingEmail ? <CircularProgress size={16} /> : <EmailIcon />
              }
              onClick={handleSendEmail}
              disabled={sendingEmail || busy || pinLoading}
              sx={{ flexShrink: 0, mt: { xs: 0, sm: 0.25 } }}>
              {sendingEmail ? 'Sending…' : 'Send email'}
            </Button>
          </Stack>

          <Button
            variant="outlined"
            color="success"
            startIcon={
              sendingWhatsApp ? <CircularProgress size={16} /> : <WhatsAppIcon />
            }
            onClick={handleContactWhatsApp}
            disabled={!selectedContact?.phone || sendingWhatsApp || busy}
            sx={{ mt: 1 }}>
            {sendingWhatsApp ? 'Opening WhatsApp…' : 'Send on WhatsApp'}
          </Button>

          {selectedContact?.phone && (
            <Typography variant="caption" color="text.secondary" display="block" mt={0.75}>
              Sending to a saved contact uses their personal PIN when personal sign-in is
              available. An existing personal PIN is re-sent unchanged.
            </Typography>
          )}

          {whatsAppError && (
            <Alert severity="error" sx={{ mt: 1 }} onClose={() => setWhatsAppError('')}>
              {whatsAppError}
            </Alert>
          )}

          {whatsAppStatus && (
            <Alert severity="success" sx={{ mt: 1 }} onClose={() => setWhatsAppStatus('')}>
              {whatsAppStatus}
            </Alert>
          )}

          {emailStatus && (
            <Alert severity="success" sx={{ mt: 1 }} onClose={() => setEmailStatus('')}>
              {emailStatus}
            </Alert>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2, flexWrap: 'wrap', gap: 1 }}>
          <Button startIcon={<PrintIcon />} onClick={handlePrint} disabled={!open || pinLoading}>
            Print
          </Button>
          <Box flex={1} />
          <Button onClick={onClose} disabled={busy}>
            Close
          </Button>
          <Button
            variant="contained"
            color={pinSet || pin ? 'warning' : 'primary'}
            startIcon={<RefreshIcon />}
            disabled={busy}
            onClick={handleRegenerate}>
            {busy ? 'Working…' : regenerateLabel}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={confirmRegenerate}
        onClose={() => setConfirmRegenerate(false)}
        maxWidth="xs"
        fullWidth>
        <DialogTitle>{confirmTitle}</DialogTitle>
        <DialogContent>
          <DialogContentText>{confirmBody}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmRegenerate(false)} disabled={busy}>
            Cancel
          </Button>
          <Button
            color="warning"
            variant="contained"
            disabled={busy}
            onClick={() => {
              setConfirmRegenerate(false);
              onRegenerate?.();
            }}>
            {confirmAction}
          </Button>
        </DialogActions>
      </Dialog>

      {open && (
        <Box className="portal-print-sheet" bgcolor="#fff">
          <Box sx={{ border: '2px solid #000', p: 3, maxWidth: 620 }}>
            <Typography variant="h5" fontWeight="bold" color="#000">
              BowlScout — {scopeLabel} portal
            </Typography>
            <Typography variant="subtitle1" color="#000" gutterBottom>
              {eventName}
            </Typography>
            <Divider sx={{ borderColor: '#000', my: 2 }} />
            <Typography variant="body1" color="#000" mb={2}>
              Scan the QR code or type the link to open the scoring portal. Enter the 5-digit PIN
              when asked.
            </Typography>
            <Box display="flex" justifyContent="center" mb={2}>
              <QRCodeData value={url} style={{ width: 210, height: 210 }} />
            </Box>
            <Typography sx={{ fontSize: 13, wordBreak: 'break-all', color: '#000', mb: 2 }}>
              {url}
            </Typography>
            {pin ? (
              <Box sx={{ borderTop: '1px solid #000', pt: 2, textAlign: 'center' }}>
                <Typography variant="overline" color="#000">
                  PIN
                </Typography>
                <Typography
                  sx={{
                    fontFamily: 'monospace',
                    fontSize: 44,
                    fontWeight: 700,
                    letterSpacing: '0.25em',
                    color: '#000'
                  }}>
                  {pin}
                </Typography>
              </Box>
            ) : (
              <Typography sx={{ fontSize: 13, borderTop: '1px solid #000', pt: 2, color: '#000' }}>
                The PIN is issued separately. Regenerate it in BowlScout if it has been lost.
              </Typography>
            )}
            <Typography sx={{ fontSize: 12, mt: 3, color: '#000' }}>
              {coverage} Keep the PIN private.
            </Typography>
          </Box>
        </Box>
      )}
    </>
  );
};

export default PortalAccessDialog;
